import React, { type ReactNode, useState } from 'react';
import { UserIcon } from '../../icons';
import { cx } from '../../../utils/cx';
import { VerifiedTick, type VerifiedTickSize } from './base-components';
import { getAvatarInitials } from './utils';
import './avatar.css';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg';

export interface AvatarProps {
  size?: AvatarSize;
  className?: string;
  src?: string | null;
  alt?: string;
  verified?: boolean;
  initials?: string;
  placeholder?: ReactNode;
}

export interface AvatarProfilePhotoProps {
  size?: AvatarSize;
  className?: string;
  src?: string | null;
  alt?: string;
  verified?: boolean;
  initials?: string;
  placeholder?: ReactNode;
}

const profileTickSizeMap: Record<AvatarSize, VerifiedTickSize> = {
  xs: 'lg',
  sm: '2xl',
  md: '3xl',
  lg: '4xl',
};

const avatarTickSizeMap: Record<AvatarSize, VerifiedTickSize> = {
  xs: 'xs',
  sm: 'sm',
  md: 'md',
  lg: 'lg',
};

function UserPlaceholderIcon({ className }: { className?: string }) {
  return (
    <span className={className} aria-hidden>
      <UserIcon className="uui-avatar-profile__placeholder-icon" size={20} />
    </span>
  );
}

export const AvatarProfilePhoto: React.FC<AvatarProfilePhotoProps> = ({
  size = 'md',
  src,
  alt = '',
  initials,
  placeholder,
  verified = false,
  className,
}) => {
  const [isFailed, setIsFailed] = useState(false);
  const displayInitials = initials ?? (alt ? getAvatarInitials(alt) : '?');
  const showImage = Boolean(src) && !isFailed;

  return (
    <div className={cx('uui-avatar-profile', `uui-avatar-profile--${size}`, className)}>
      <div className="uui-avatar-profile__ring">
        <div className="uui-avatar-profile__content">
          {showImage ? (
            <img
              src={src!}
              alt={alt}
              className="uui-avatar-profile__image"
              onError={() => setIsFailed(true)}
            />
          ) : initials || alt ? (
            <span aria-hidden="true">{displayInitials}</span>
          ) : (
            placeholder ?? <UserPlaceholderIcon className="uui-avatar-profile__placeholder-icon" />
          )}
        </div>
      </div>
      {verified && (
        <VerifiedTick size={profileTickSizeMap[size]} className="uui-avatar-profile__badge" />
      )}
    </div>
  );
};

/** Smaller circular avatar for topbar, cards, etc. */
export const Avatar: React.FC<AvatarProps> = ({
  size = 'md',
  src,
  alt = '',
  initials,
  placeholder,
  verified = false,
  className,
}) => {
  const [isFailed, setIsFailed] = useState(false);
  const displayInitials = initials ?? (alt ? getAvatarInitials(alt) : '?');
  const showImage = Boolean(src) && !isFailed;

  return (
    <div className={cx('uui-avatar', `uui-avatar--${size}`, className)}>
      <div className="uui-avatar__content">
        {showImage ? (
          <img
            src={src!}
            alt={alt}
            className="uui-avatar__image"
            onError={() => setIsFailed(true)}
          />
        ) : initials || alt ? (
          <span aria-hidden="true">{displayInitials}</span>
        ) : (
          placeholder ?? <UserPlaceholderIcon className="uui-avatar-profile__placeholder-icon" />
        )}
      </div>
      {verified && <VerifiedTick size={avatarTickSizeMap[size]} className="uui-avatar__badge" />}
    </div>
  );
};
